import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { postCategories, postSummary, postSummaryRefresh } from './api';
import { adaptCategories, adaptSummary, hourLabel, type Adapted } from './adapt';
import { ATTEMPT_MIX, AUTOMATION, CALLS, HOURLY, NO_AGENT_ORDERS, ROWS, AGENTS, buildCatalog, buildTrend } from './mockData';
import { applyFilters, filterCalls, totals } from './stats';
import type { CatalogCategory, Filters } from './types';

export interface ReportData extends Adapted {
  mode: 'live' | 'sample';
  loading: boolean;
  error: string | null;
  /** Every agent seen so far with no agent filter on, so choosing one does not shrink the list. */
  agentOptions: string[];
  catalog: CatalogCategory[];
  filters: Filters;
  /** The id the server filters by; null = everyone. */
  agentId: number | null;
  /** Resolve an agent name shown on screen to what the server needs. Unassigned work has no id. */
  /** Super admins: rebuild the current view from live data now. */
  refreshLive: () => Promise<void>;
  /** Nothing loads until this is called (the Run report button); filter edits alone never hit the server. */
  run: () => void;
  /** The filters as they were when the report was last run: what the numbers on screen, the hourly rows and drilldowns use. */
  ranFilters: Filters;
  /** Counts up on every run; anything fetched for an earlier run is stale. */
  runToken: number;
  /** The report has been run at least once (always true for sample data). */
  hasRun: boolean;
  /** Filters changed since the last run, so what is on screen is not what the filters now say. */
  stale: boolean;
  resolveAgent: (name: string | 'all') => { agentId: number | null; noAgent: boolean };
}

const Ctx = createContext<ReportData | null>(null);

export function useReport(): ReportData {
  const v = useContext(Ctx);
  if (!v) throw new Error('useReport must be used inside <ReportProvider>');
  return v;
}

const EMPTY: Adapted = {
  rows: [], calls: [], agents: [], agentIdByName: {}, automation: { engatiAi: 0, abcAutoUpdated: 0, otherSystem: 0 },
  noAgent: { orders: 0, value: 0 }, trend: [], hourly: [], attempts: { customers: 0, a1: 0, a2: 0, a3: 0, a4plus: 0, never: 0 },
  notInConnectivity: { inbound: 0, dialerClicks: 0, aiCalls: 0 }, uniqueCustomers: 0, customersByBucket: {}, coverage: {}, asOf: '', ownOnly: false,
  minTalkSeconds: 10, lockDays: 15,
};

/** The sample set: same shapes as the live adapter, so the tabs cannot tell the difference. */
function sampleData(f: Filters): Adapted {
  const rows = applyFilters(ROWS, f);
  const calls = filterCalls(CALLS, f);
  const t = totals(rows);
  const shares = HOURLY.map(h => ({ hour: hourLabel(h.h), h }));
  const dials = calls.reduce((a, c) => a + c.dials, 0);
  const rest = 1 - ATTEMPT_MIX.reduce((a, b) => a + b, 0);
  const customers = calls.reduce((a, c) => a + c.customersCalled, 0);
  return {
    ...EMPTY, rows, calls, agents: AGENTS, automation: AUTOMATION,
    noAgent: { orders: NO_AGENT_ORDERS.orders, value: NO_AGENT_ORDERS.value },
    trend: buildTrend(t.validOrders, t.cancelledOrders),
    hourly: shares.map(({ hour, h }) => ({ hour, dials: Math.round(dials * h.share), connected: Math.round(dials * h.share * h.connect) })),
    attempts: {
      customers, a1: Math.round(customers * ATTEMPT_MIX[0]), a2: Math.round(customers * ATTEMPT_MIX[1]),
      a3: Math.round(customers * ATTEMPT_MIX[2]), a4plus: Math.round(customers * ATTEMPT_MIX[3]), never: Math.round(customers * rest),
    },
    notInConnectivity: { inbound: 3187, dialerClicks: 1489, aiCalls: 2024 },
    uniqueCustomers: t.uniqueCustomers, asOf: new Date().toISOString(),
  };
}

export const ReportProvider: React.FC<{ filters: Filters; sample: boolean; children: React.ReactNode }> = ({ filters, sample, children }) => {
  const [live, setLive] = useState<Adapted>(EMPTY);
  const [liveCatalog, setLiveCatalog] = useState<CatalogCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentOptions, setAgentOptions] = useState<string[]>([]);
  const known = useRef<Record<string, number>>({});
  const serial = useRef(0);
  const [runToken, setRunToken] = useState(0);
  const [ranKey, setRanKey] = useState<string | null>(null);
  const [ranFilters, setRanFilters] = useState<Filters>(filters);

  // The server filters by agent and bucket; resolve the agent name chosen in the filter to its id.
  const agentId = filters.agent === 'all' ? null : (known.current[filters.agent] ?? null);
  const key = `${filters.from}|${filters.fromTime}|${filters.to}|${filters.toTime}|${filters.grouping}|${filters.agent}|${filters.buckets.join(',')}`;

  const run = () => { setRanKey(key); setRanFilters(filters); setRunToken(t => t + 1); };

  useEffect(() => {
    if (sample || runToken === 0) return;       // the report runs only when the Run report button is pressed
    const mine = ++serial.current;
    const timer = window.setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const [s, c] = await Promise.all([postSummary(filters, agentId), postCategories({ ...filters, category: 'all', product: 'all' }, agentId)]);
        if (mine !== serial.current) return;            // a newer request is already running
        if (!s?.success) throw new Error('The report could not be loaded');
        const a = adaptSummary(s);
        Object.assign(known.current, a.agentIdByName);
        setLive(a); setLiveCatalog(adaptCategories(c));
        if (filters.agent === 'all') setAgentOptions(prev => Array.from(new Set([...prev, ...a.agents])).sort((x, y) => x.localeCompare(y)));
      } catch (e) {
        if (mine === serial.current) setError(e instanceof Error ? e.message : 'The report could not be loaded');
      } finally {
        if (mine === serial.current) setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [runToken, sample]);

  const refreshLive = async () => {
    setLoading(true); setError(null);
    try {
      const s = await postSummaryRefresh(filters, agentId);
      if (!s?.success) throw new Error('The report could not be refreshed');
      const a = adaptSummary(s);
      Object.assign(known.current, a.agentIdByName);
      setLive(a);
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || (e instanceof Error ? e.message : 'The report could not be refreshed'));
    } finally {
      setLoading(false);
    }
  };

  const sampleAdapted = useMemo(() => (sample ? sampleData(filters) : null), [sample, filters]);
  const data = sample ? (sampleAdapted as Adapted) : live;
  const catalog = useMemo(() => (sample ? buildCatalog(ROWS, 'all', 'all') : liveCatalog), [sample, liveCatalog]);

  const value = useMemo<ReportData>(() => ({
    ...data, mode: sample ? 'sample' : 'live', loading: sample ? false : loading, error: sample ? null : error,
    agentOptions: sample ? AGENTS : (agentOptions.length ? agentOptions : data.agents), catalog, filters, agentId, refreshLive,
    ranFilters: sample || runToken === 0 ? filters : ranFilters, runToken,
    run, hasRun: sample || runToken > 0, stale: !sample && runToken > 0 && ranKey !== key,
    resolveAgent: (name: string | 'all') => {
      if (name === 'all') return { agentId: null, noAgent: false };
      if (name === 'Unassigned' || name === 'Unmatched agent' || name === 'No agent tag') return { agentId: null, noAgent: true };
      return { agentId: known.current[name] ?? null, noAgent: false };
    },
  }), [data, sample, loading, error, agentOptions, catalog, filters, agentId, runToken, ranKey, key, ranFilters]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
